import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Rocket, Sparkles, Wind, BarChart3, ArrowRight, CheckCircle2, TrendingUp,
} from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { trackEvent } from "@/access/tracker";

/**
 * DiscChapters — the structural DiSC backbone for any page.
 *
 * Renders 4 large alternating chapters explicitly tagged D / i / S / C.
 * Each chapter carries: persona, eyebrow, title, body, 3 outcome bullets,
 * 1 highlight stat, and a tailored CTA. This is the spine that gives every
 * page a guaranteed DiSC voice — not just a chip on top.
 *
 * Usage:
 *   <DiscChapters
 *      heading="Four mindsets. One platform."
 *      chapters={[
 *        { disc: "D", eyebrow: "Driver — fast & focused", title: "...", body: "...",
 *          bullets: ["...","...","..."], stat: { value: "+24%", label: "lead → booking" },
 *          cta: { label: "Show me the ROI", action: "demo" } },
 *        ...3 more...
 *      ]}
 *   />
 */

const PERSONA_META = {
  D: {
    Icon: Rocket,
    accent: "from-rose-500 to-orange-500",
    chip:  "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
    soft:  "bg-rose-500/[0.06] dark:bg-rose-500/[0.07]",
    ring:  "ring-rose-500/30",
    persona: "scale",
  },
  i: {
    Icon: Sparkles,
    accent: "from-violet-500 to-fuchsia-500",
    chip:  "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
    soft:  "bg-violet-500/[0.05] dark:bg-violet-500/[0.07]",
    ring:  "ring-violet-500/30",
    persona: "comms",
  },
  S: {
    Icon: Wind,
    accent: "from-emerald-500 to-teal-500",
    chip:  "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    soft:  "bg-emerald-500/[0.05] dark:bg-emerald-500/[0.07]",
    ring:  "ring-emerald-500/30",
    persona: "calm",
  },
  C: {
    Icon: BarChart3,
    accent: "from-blue-500 to-sky-500",
    chip:  "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300",
    soft:  "bg-blue-500/[0.05] dark:bg-blue-500/[0.07]",
    ring:  "ring-blue-500/30",
    persona: "data",
  },
};

const Chapter = ({ chapter, index }) => {
  const meta = PERSONA_META[chapter.disc] || PERSONA_META.D;
  const Icon = meta.Icon;
  const flip = index % 2 === 1; // alternate visual side
  const navigate = useNavigate();
  const openDemo = useDemo();

  const onCta = () => {
    trackEvent("disc_chapter_click", {
      persona: meta.persona,
      meta: {
        disc: chapter.disc,
        from_path: typeof window !== "undefined" ? window.location.pathname : "",
        action: chapter.cta?.action || "none",
      },
    });
    if (chapter.cta?.action === "demo") openDemo();
    else if (chapter.cta?.to) {
      // In-page anchor links (e.g. "#platform-capabilities") should smooth-scroll
      // instead of triggering a route change.
      if (chapter.cta.to.startsWith("#")) {
        const id = chapter.cta.to.slice(1);
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate(chapter.cta.to);
      }
    }
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
      data-testid={`disc-chapter-block-${chapter.disc.toLowerCase()}`}
      className={`relative overflow-hidden rounded-3xl border border-border ${meta.soft} px-6 py-10 shadow-sm sm:px-10 sm:py-14`}
    >
      {/* Persona-tinted ambient glow */}
      <div className={`pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-60 blur-[100px] ${flip ? "" : "bg-gradient-to-br"} ${meta.accent}`} style={{ background: undefined }} />
      <div className={`pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gradient-to-br opacity-25 blur-[100px] ${meta.accent}`} />

      <div className={`relative grid items-center gap-8 sm:gap-12 lg:grid-cols-[1.1fr_1fr] ${flip ? "lg:grid-flow-dense" : ""}`}>
        {/* Copy column */}
        <div className={flip ? "lg:col-start-2" : ""}>
          <span
            data-testid={`disc-chapter-eyebrow-${chapter.disc.toLowerCase()}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.18em] ${meta.chip}`}
          >
            <Icon className="h-3 w-3" />
            {chapter.eyebrow}
          </span>

          <h3 className="mt-4 font-display text-[1.75rem] font-black leading-[1.05] tracking-[-0.025em] sm:text-[2.25rem] lg:text-[2.5rem]">
            {chapter.title}
          </h3>

          <p className="mt-4 max-w-xl text-[14.5px] leading-relaxed text-muted-foreground sm:text-[15.5px]">
            {chapter.body}
          </p>

          {chapter.bullets?.length > 0 && (
            <ul className="mt-5 space-y-2">
              {chapter.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[13.5px] font-medium text-foreground/85">
                  <CheckCircle2 className={`mt-0.5 h-4 w-4 flex-none bg-clip-text ${meta.chip.includes("rose") ? "text-rose-500" : meta.chip.includes("violet") ? "text-violet-500" : meta.chip.includes("emerald") ? "text-emerald-500" : "text-blue-500"}`} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {chapter.cta && (
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                data-testid={`disc-chapter-cta-${chapter.disc.toLowerCase()}`}
                onClick={onCta}
                className="group inline-flex h-11 items-center gap-2 rounded-xl bg-foreground px-5 text-[13.5px] font-bold text-background transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                {chapter.cta.label}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              {chapter.secondary && (
                <Link
                  to={chapter.secondary.to}
                  className="inline-flex h-11 items-center gap-2 rounded-xl border border-foreground/15 bg-card px-4 text-[13px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
                >
                  {chapter.secondary.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Stat card column */}
        <div className={flip ? "lg:col-start-1 lg:row-start-1" : ""}>
          <div className={`relative overflow-hidden rounded-2xl border border-border bg-card p-6 ring-1 ring-inset ${meta.ring} sm:p-8`}>
            <div className={`pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-gradient-to-br opacity-25 blur-3xl ${meta.accent}`} />

            <p className="relative text-[10.5px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              The number that matters
            </p>

            <p className="relative mt-3 inline-flex items-baseline gap-2 font-display">
              <span className={`bg-gradient-to-br bg-clip-text text-[3.25rem] font-black leading-none tracking-[-0.04em] text-transparent sm:text-[4rem] ${meta.accent}`}>
                {chapter.stat?.value}
              </span>
              {chapter.stat?.suffix && (
                <span className="text-base font-bold text-muted-foreground">{chapter.stat.suffix}</span>
              )}
            </p>

            <p className="relative mt-3 text-[13px] leading-snug text-muted-foreground sm:text-[13.5px]">
              {chapter.stat?.label}
            </p>

            {chapter.stat?.compare && (
              <div className="relative mt-5 flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2.5">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                <span className="text-[11.5px] font-semibold text-foreground/85">{chapter.stat.compare}</span>
              </div>
            )}

            {chapter.stat?.source && (
              <p className="relative mt-3 text-[10.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground/70">
                {chapter.stat.source}
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export const DiscChapters = ({
  eyebrow = "Built for four kinds of clinic owners",
  heading = "Same platform. Four mindsets.",
  sub = "Every clinic owner reads a SaaS site differently. Here's the same product, four ways — each chapter speaks to one of you.",
  chapters = [],
  className = "",
  showHeader = true,
}) => {
  return (
    <section
      data-testid="disc-chapters-section"
      className={`relative py-16 sm:py-24 ${className}`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {showHeader && (
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-foreground/15 bg-card px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-foreground/75">
              <Sparkles className="h-3 w-3" />
              {eyebrow}
            </span>
            <h2 className="mt-4 font-display text-[2rem] font-black leading-[1.04] tracking-[-0.025em] sm:text-[2.75rem] lg:text-[3.25rem]">
              {heading}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-[14.5px] leading-relaxed text-muted-foreground sm:text-base">
              {sub}
            </p>
          </div>
        )}

        <div className={`grid grid-cols-1 gap-5 ${showHeader ? "mt-12 sm:mt-16" : "mt-2"} sm:gap-6`}>
          {chapters.map((c, i) => (
            <Chapter key={c.disc + "-" + i} chapter={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DiscChapters;
